import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';

export default function GLBViewer({ url, style, onLoadFinish, format }) {
  const mountRef = useRef(null);
  useEffect(() => {
    const container = mountRef.current;

    // 场景
    const scene = new THREE.Scene();

    // 相机
    const camera = new THREE.PerspectiveCamera(
      75,
      container.clientWidth / container.clientHeight,
      0.1,
      1000,
    );
    camera.position.set(0, 1, 3);

    // 渲染器
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(renderer.domElement);

    // 控制器
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true; // 平滑过渡
    controls.enableZoom = true;
    controls.enablePan = true;
    controls.enableRotate = true;

    // 灯光
    // 添加灯光（必要）
    const ambientLight = new THREE.AmbientLight(0xffffff, 1); // 环境光
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(5, 10, 7.5);
    scene.add(directionalLight);

    const addModel = (model) => {
      // 1. 计算模型包围盒
      const box = new THREE.Box3().setFromObject(model);
      const size = new THREE.Vector3();
      const center = new THREE.Vector3();
      box.getSize(size); // 模型尺寸
      box.getCenter(center); // 模型中心点

      // 2. 将模型移动到原点
      model.position.sub(center);

      // 3. 设置合适的相机位置（根据模型大小）
      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = camera.fov * (Math.PI / 180); // 摄像机角度转弧度
      const cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
      camera.position.set(0, 0, cameraZ * 1.5);
      camera.lookAt(0, 0, 0);

      // 4. 添加模型
      scene.add(model);
      controls.update();
      onLoadFinish();
    };

    const loadGlb = () => {
      // 加载 GLB 模型
      const loader = new GLTFLoader();
      console.log('url', url);
      loader.load(url, (gltf) => {
        const model = gltf.scene;
        addModel(model);
      });
    };

    const loadFbx = () => {
      const loader = new FBXLoader();
      loader.load(
        url,
        (fbx) => {
          addModel(fbx);
        },
        undefined,
        (error) => {
          console.error('FBX 加载失败:', error);
        },
      );
    };

    const loadObj = () => {
      const loader = new OBJLoader();
      loader.load(
        url,
        (obj) => {
          addModel(obj);
        },
        undefined,
        (error) => {
          console.error('OBJ 加载失败:', error);
        },
      );
    };

    if (format === 'glb') {
      loadGlb();
    } else if (format === 'fbx') {
      loadFbx();
    } else if (format === 'obj') {
      loadObj();
    }

    // 动画循环
    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // 监听尺寸变化
    const handleResize = () => {
      camera.aspect = container.clientWidth / container.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(container.clientWidth, container.clientHeight);
    };
    window.addEventListener('resize', handleResize);

    // 清理资源
    return () => {
      window.removeEventListener('resize', handleResize);
      container.removeChild(renderer.domElement);
      renderer.dispose();
      controls.dispose();
    };
  }, []);

  return <div ref={mountRef} style={style} />;
}
